import { ReadonlyVec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { ImageObject } from '../../../data/image/ImageObject';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformInt } from '../../utils/uniforms/UniformInt';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../utils/uniforms/UniformVec3';
import { Renderer } from '../Renderer';
import {
    checkerboardImageFragmentShader,
    checkerboardImageVertexShader,
    defaultImageFragmentShader,
    defaultImageVertexShader,
    originalImageFragmentShader,
    originalImageVertexShader,
    petFusionImageFragmentShader,
    petFusionImageVertexShader,
} from './glsl';
import { Modality } from '~/domain/dicom/modality';

export type ImageRenderingOptions = {
    alpha: number;
    checkerboardDensity: number;
    viewMode: ImageViewMode;
    colorMask: ReadonlyVec3;
    secondaryColorMask: ReadonlyVec3;
    ifPetImageFused?: boolean;
    petFusionColorMapTypeIdx?: number;
};

export enum ImageViewMode {
    Checkerboard = 'checkerboard',
    AlphaBlending = 'alpha-blending',
}

/**
 * Renderer that will render {@link ImageObject ImageObjects}.
 */
export class ImageRenderer implements Renderer {
    /**
     * The default shader that will render {@link ImageObject ImageObjects} on {@link PlaneView}
     * without deformations or other fancy stuff.
     */
    private defaultShader: DefaultImageShader;

    private originalShader: OriginalImageShader;

    private checkerboardShader: CheckerBoardImageShader;

    private petFusionShader: PetFusionImageShader;

    public constructor() {
        this.defaultShader = new DefaultImageShader();
        this.originalShader = new OriginalImageShader();
        this.checkerboardShader = new CheckerBoardImageShader();
        this.petFusionShader = new PetFusionImageShader();
    }

    /**
     * Method to delete the {@link ImageRenderer}.\
     * This will deallocate the shaders after which this {@link ImageRenderer} should no longer be used.
     */
    public delete(): void {
        this.defaultShader.delete();
        this.originalShader.delete();
        this.checkerboardShader.delete();
        this.petFusionShader.delete();
    }

    /**
     * Method to render {@link ImageObject ImageObjects}.
     * @throws if {@link view} of unsupported type is provided. Currently only {@link PlaneView PlaneViews} are supported.
     * @param view The {@link PlaneView} whose {@link PlaneView.projectionViewMatrix projectionViewMatrix} is used for projection.
     * @param primaryImage The primary {@link ImageObject} to render.
     * @param secondaryImage Optional secondary {@link ImageObject} to render (e.g., for fusion).
     * @returns True if the images were successfully rendered, false otherwise.
     */
    public render(
        view: TextureView,
        primaryImage: ImageObject | undefined,
        secondaryImage: ImageObject | undefined,
        renderingOptions: ImageRenderingOptions
    ): boolean {
        if (!primaryImage) {
            return false;
        }
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);

            // Alpha blending and checkerboard are fusion-only visualizations.
            // With only a primary image present they must not apply, or the
            // primary would render half-transparent or in a checker pattern
            // if stale blending state leaked through when the secondary was
            // unloaded.
            const effectiveOptions: ImageRenderingOptions = secondaryImage
                ? renderingOptions
                : {
                      ...renderingOptions,
                      viewMode: ImageViewMode.AlphaBlending,
                      alpha: 1,
                      ifPetImageFused: false,
                  };

            this.renderImage(view, primaryImage, true, effectiveOptions);

            if (secondaryImage) {
                this.renderImage(view, secondaryImage, false, effectiveOptions);
            }

            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ImageRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    private renderImage(
        view: PlaneView,
        image: ImageObject | undefined,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions
    ): void {
        if (!image) {
            return;
        }
        const vao = image.crossSection(view.projectionViewMatrix);
        if (vao) {
            image.bindTexture();
            vao.bind(0, 1);
            switch (renderingOptions.viewMode) {
                case ImageViewMode.Checkerboard:
                    this.renderCheckerboard(
                        image,
                        isPrimary,
                        renderingOptions,
                        vao.indexCount
                    );
                    break;
                case ImageViewMode.AlphaBlending:
                default:
                    const isPetImage =
                        renderingOptions.ifPetImageFused &&
                        image.parent.seriesMetadata.modality === Modality.PT;
                    if (isPetImage) {
                        this.renderPetFusion(
                            image,
                            renderingOptions,
                            vao.indexCount
                        );
                    } else {
                        this.renderDefault(
                            image,
                            isPrimary,
                            renderingOptions,
                            vao.indexCount
                        );
                    }
                    break;
            }

            vao.unbind(0, 1);
            vao.delete();
        }
    }
    /**
     * Method to render {@link ImageObject ImageObjects}.
     * @throws if {@link view} of unsupported type is provided. Currently only {@link PlaneView PlaneViews} are supported.
     * @param view The {@link PlaneView} whose {@link PlaneView.projectionViewMatrix projectionViewMatrix} is used for projection.
     * @param images An array of {@link ImageObject ImageObjects} that are to be rendered.
     * @returns True if the {@link images} were successfully rendered, false otherwise.
     */
    public renderOriginal(view: TextureView, images: ImageObject[]): boolean {
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);

            this.originalShader.start();
            for (const image of images) {
                const vao = image.crossSection(view.projectionViewMatrix);
                if (vao) {
                    image.bindTexture();
                    this.originalShader.minValue.loadFloat(image.minValue);

                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
            this.originalShader.stop();
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ImageRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    /**
     * Method to perform the image rendering using the {@link DefaultImageShader}.
     * This method {@link ShaderProgram.start binds} the {@link DefaultImageShader},
     * Loads values to uniform variables,
     * {@link gl.drawElements renders the triangles},
     * and {@link ShaderProgram.stop unbinds} the {@link DefaultImageShader}.
     *
     * @param image The {@link ImageObject} that is being rendered.
     * Used to get the values for uniform variables.
     * @param isPrimary Whether this is the primary image.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderDefault(
        image: ImageObject,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        indexCount: number
    ): void {
        this.defaultShader.start();
        this.defaultShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.defaultShader.colorMask.loadVec3(
            isPrimary
                ? renderingOptions.colorMask
                : renderingOptions.secondaryColorMask
        );
        this.defaultShader.alpha.loadFloat(
            isPrimary ? renderingOptions.alpha : 1 - renderingOptions.alpha
        );

        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.defaultShader.stop();
    }

    /**
     * Method to perform PET fusion rendering with hot colormap.
     * Applies window/level normalization and selectedRange thresholding
     * to display PET data with clinical colormap visualization.
     *
     * @param image The PET {@link ImageObject} that is being rendered.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderPetFusion(
        image: ImageObject,
        renderingOptions: ImageRenderingOptions,
        indexCount: number
    ): void {
        this.petFusionShader.start();
        this.petFusionShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.petFusionShader.alpha.loadFloat(1 - renderingOptions.alpha);

        const colormapTypeIdx = renderingOptions.petFusionColorMapTypeIdx ?? 0;
        this.petFusionShader.colormapType.loadInt(colormapTypeIdx);

        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.petFusionShader.stop();
    }

    /**
     * Method to perform checkerboard rendering for image comparison.
     *
     * @param image The {@link ImageObject} that is being rendered.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderCheckerboard(
        image: ImageObject,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        indexCount: number
    ): void {
        this.checkerboardShader.start();
        this.checkerboardShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.checkerboardShader.colorMask.loadVec3(
            isPrimary
                ? renderingOptions.colorMask
                : renderingOptions.secondaryColorMask
        );
        this.checkerboardShader.alpha.loadFloat(
            isPrimary ? renderingOptions.alpha : 1 - renderingOptions.alpha
        );
        this.checkerboardShader.isPrimary.loadInt(isPrimary ? 1 : 0);
        this.checkerboardShader.checkerboardDensity.loadFloat(
            renderingOptions.checkerboardDensity
        );
        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.checkerboardShader.stop();
    }
}

/**
 * {@link ShaderProgram} that will render {@link ImageObject}-{@link PlaneView} intersections.
 * The shader has two inputs:
 * 1. in vec2 position, the intersection vertex coordinates in destination texture space.
 * 2. in vec3 textureCoords, which are the 3D texture coordinates of the image.
 *
 * It also has 3 uniform variables:
 * 1. uniform vec2 windowLevel, which is the {@link ImageObject.windowingLevel windowingLevel} of the {@link ImageObject}.
 * 2. uniform vec3 colorMask, which is the {@link ImageObject.colorMask colorMask} of the {@link ImageObject}.
 * 3. uniform float alpha, which is the {@link ImageObject.alpha alpha} of the {@link ImageObject}.
 */
class DefaultImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = defaultImageVertexShader;
    private static readonly FRAGMENT_CONTENT = defaultImageFragmentShader;

    public readonly windowLevel: UniformVec2;
    public readonly colorMask: UniformVec3;
    public readonly alpha: UniformFloat;

    constructor() {
        super(
            DefaultImageShader.VERTEX_CONTENT,
            DefaultImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.colorMask = new UniformVec3('colorMask');
        this.alpha = new UniformFloat('alpha');

        this.storeUniformLocations(
            this.windowLevel,
            this.colorMask,
            this.alpha
        );
    }
}

/**
 * {@link ShaderProgram} for checkerboard view mode.
 * Used to compare two images by showing alternating squares from each.
 */
class CheckerBoardImageShader extends ShaderProgram {
    public readonly windowLevel: UniformVec2;
    public readonly colorMask: UniformVec3;
    public readonly alpha: UniformFloat;
    public readonly isPrimary: UniformInt;
    public readonly checkerboardDensity: UniformFloat;

    constructor() {
        super(
            checkerboardImageVertexShader,
            checkerboardImageFragmentShader,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.colorMask = new UniformVec3('colorMask');
        this.alpha = new UniformFloat('alpha');
        this.isPrimary = new UniformInt('isPrimary');
        this.checkerboardDensity = new UniformFloat('checkerboardDensity');

        this.storeUniformLocations(
            this.windowLevel,
            this.colorMask,
            this.alpha,
            this.isPrimary,
            this.checkerboardDensity
        );
    }
}

/**
 * {@link ShaderProgram} for rendering original image values without windowing.
 */
class OriginalImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = originalImageVertexShader;
    private static readonly FRAGMENT_CONTENT = originalImageFragmentShader;

    public readonly minValue: UniformFloat;

    constructor() {
        super(
            OriginalImageShader.VERTEX_CONTENT,
            OriginalImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.minValue = new UniformFloat('minValue');
        this.storeUniformLocations(this.minValue);
    }
}

/**
 * {@link ShaderProgram} for PET fusion rendering.
 *
 * The shader applies:
 * 1. Window/level normalization for display range
 * 2. PET fusion colormap mapping (e.g. hot colormap: black -> red -> yellow -> white)
 *
 * Uniforms:
 * - windowLevel: vec2 [window, level] for normalization
 * - alpha: float opacity (0-1)
 * - colormapType: int index selecting the PET fusion colormap
 */
class PetFusionImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = petFusionImageVertexShader;
    private static readonly FRAGMENT_CONTENT = petFusionImageFragmentShader;

    public readonly windowLevel: UniformVec2;
    public readonly alpha: UniformFloat;
    public readonly colormapType: UniformInt;

    constructor() {
        super(
            PetFusionImageShader.VERTEX_CONTENT,
            PetFusionImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.alpha = new UniformFloat('alpha');
        this.colormapType = new UniformInt('colormapType');

        this.storeUniformLocations(
            this.windowLevel,
            this.alpha,
            this.colormapType
        );
    }
}
